import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../api/apiService';

// Format timestamp to readable string
const formatTime = (isoString) => {
  if (!isoString) return '-';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Get display name for task type
const getTaskTypeDisplay = (taskType) => {
  const mapping = {
    'feature_title_extraction': 'Feature Title',
    'attribute_title_extraction': 'Attribute Title',
    'feature_detail_extraction': 'Feature Detail',
    'attribute_detail_extraction': 'Attribute Detail'
  };
  return mapping[taskType] || taskType;
};

// Status indicator component
const StatusDot = ({ status }) => {
  const colors = {
    completed: 'bg-green-500',
    failed: 'bg-red-500',
    in_progress: 'bg-blue-500 animate-pulse',
    pending: 'bg-gray-400'
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || colors.pending}`} />;
};

// Task item component for inline display
const TaskItem = ({ task }) => {
  const isSuccess = task.status === 'completed';
  const isFailed = task.status === 'failed';
  const isInProgress = task.status === 'in_progress';

  return (
    <div className="py-2 px-3 mb-2 rounded-lg bg-gray-50 border border-gray-200">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-2">
          <StatusDot status={task.status} />
          <span className="font-medium text-sm text-gray-700">{getTaskTypeDisplay(task.task_type)}</span>
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          isSuccess ? 'bg-green-100 text-green-700' :
          isFailed ? 'bg-red-100 text-red-700' :
          isInProgress ? 'bg-blue-100 text-blue-700 animate-pulse' :
          'bg-gray-100 text-gray-600'
        }`}>
          {isSuccess ? 'Completed' : isFailed ? 'Failed' : isInProgress ? 'Running' : 'Pending'}
        </span>
      </div>
      <div className="text-xs text-gray-500 flex items-center gap-3">
        <span>Created: {formatTime(task.created_at)}</span>
        {task.updated_at && isSuccess && <span>• Finished: {formatTime(task.updated_at)}</span>}
        {isFailed && task.error && (
          <span className="text-red-600" title={task.error}>
            • Error: {task.error.length > 50 ? task.error.substring(0, 50) + '...' : task.error}
          </span>
        )}
      </div>
    </div>
  );
};

// Project row with expandable task details
const ProjectRow = ({ project, onDelete }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [hasActiveTasks, setHasActiveTasks] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!isExpanded) return;

    setLoadingTasks(true);
    try {
      const result = await apiService.getProjectTasks(project.id);
      // Show all tasks, no time limit
      const allTasks = result.tasks || [];
      setTasks(allTasks);
      setHasActiveTasks(allTasks.some(t => t.status === 'in_progress' || t.status === 'pending'));
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
    } finally {
      setLoadingTasks(false);
    }
  }, [isExpanded, project.id]);

  // Fetch tasks when expanded
  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Poll for active tasks
  useEffect(() => {
    if (!hasActiveTasks || !isExpanded) return;

    const interval = setInterval(fetchTasks, 3000);
    return () => clearInterval(interval);
  }, [hasActiveTasks, isExpanded, fetchTasks]);

  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const failedCount = tasks.filter(t => t.status === 'failed').length;
  const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
  const pendingCount = tasks.filter(t => t.status === 'pending').length;

  return (
    <>
      <tr 
        className="hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {project.id}
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className="text-sm font-medium text-gray-900">{project.name}</span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(project);
            }}
            className="text-red-600 hover:text-red-900 font-medium"
          >
            Delete
          </button>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-10">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            <svg
              className={`w-4 h-4 text-gray-600 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan="4" className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-700">Project Status & Background Tasks</h4>
                <div className="flex gap-2 text-xs">
                  {inProgressCount > 0 && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                      ⏳ {inProgressCount} running
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full font-medium">
                      ⏸ {pendingCount} pending
                    </span>
                  )}
                  {completedCount > 0 && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full font-medium">
                      ✓ {completedCount} completed
                    </span>
                  )}
                  {failedCount > 0 && (
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full font-medium">
                      ✗ {failedCount} failed
                    </span>
                  )}
                  {tasks.length === 0 && !loadingTasks && (
                    <span className="text-gray-500 italic">No tasks yet</span>
                  )}
                </div>
              </div>

              {loadingTasks && tasks.length === 0 ? (
                <div className="flex items-center gap-2 text-gray-500 py-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm">Loading tasks...</span>
                </div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-sm">No background tasks for this project</p>
                  <p className="text-xs mt-1">Tasks will appear here when processing starts</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {tasks.map(task => (
                    <TaskItem key={task.task_id} task={task} />
                  ))}
                </div>
              )}

              {hasActiveTasks && (
                <div className="mt-3 text-xs text-gray-500 text-center">
                  Auto-refreshing every 3s...
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const ManageProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    fetchProjects();
    setSuccess(null); // Clear any success message when component mounts
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setSuccess(null); // Clear success message when fetching
      const projectList = await apiService.getProjects();
      setProjects(projectList);
    } catch (err) {
      setError('Failed to fetch projects: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectName) => {
    if (!confirmDelete) {
      setError('Please confirm the deletion by checking the confirmation box');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const result = await apiService.deleteProject(projectName);
      console.log('Delete result:', result); // For debugging

      // Remove from local state
      setProjects(projects.filter(p => p.name !== projectName));
      setDeletingProject(null);
      setConfirmDelete(false);

      // Show success message
      setSuccess(`Project '${projectName}' deleted successfully!`);
      setTimeout(() => setSuccess(null), 5000); // Clear success message after 5 seconds
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete project: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6">Manage Projects</h2>
      
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-blue-700">View and manage your projects. You can delete projects that are no longer needed.</p>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-700">{success}</p>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      )}
      
      {!loading && projects.length === 0 && (
        <div className="text-center py-10">
          <p className="text-gray-500">No projects available. Upload a project first to see it in this list.</p>
        </div>
      )}
      
      {!loading && projects.length > 0 && (
        <div>
          <h3 className="text-xl font-semibold mb-4">Available Projects ({projects.length})</h3>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => (
                  <ProjectRow
                    key={project.id}
                    project={project}
                    onDelete={(proj) => setDeletingProject(proj)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {deletingProject && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <h3 className="text-lg font-medium text-gray-900">
                Confirm Deletion of '{deletingProject.name}'
              </h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  You are about to delete project: <strong>{deletingProject.name}</strong>
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  This will permanently remove:
                </p>
                <ul className="mt-2 text-left text-sm text-gray-500 list-disc list-inside">
                  <li>All embeddings and vector data for this project</li>
                  <li>All features, subfeatures, and attributes associated with this project</li>
                  <li>All logs and costs related to this project</li>
                  <li>The project's source code directory</li>
                  <li>Any uploaded files for this project</li>
                </ul>
              </div>
              
              <div className="mt-4">
                <div className="flex items-start mb-4">
                  <div className="flex items-center h-5">
                    <input
                      id="confirm-delete"
                      type="checkbox"
                      checked={confirmDelete}
                      onChange={(e) => setConfirmDelete(e.target.checked)}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="confirm-delete" className="font-medium text-gray-700">
                      I understand that deleting '{deletingProject.name}' will permanently remove all related data
                    </label>
                  </div>
                </div>
                
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => handleDeleteProject(deletingProject.name)}
                    disabled={!confirmDelete}
                    className={`py-2 px-4 rounded-md text-white font-medium ${
                      !confirmDelete
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    Yes, Delete
                  </button>
                  <button
                    onClick={() => {
                      setDeletingProject(null);
                      setConfirmDelete(false);
                    }}
                    className="py-2 px-4 bg-gray-500 text-white rounded-md hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProjects;